const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value ?? {}, key);

const resolveExplicitValue = (value, key, fallback) =>
  hasOwn(value, key) ? value[key] : fallback;

const normalizeSource = (value) => String(value ?? '').trim().toLowerCase();

const resolvePublicationTrack = (publication = null) =>
  publication?.track?.mediaStreamTrack ?? publication?.track ?? null;

const resolveTrackKind = (publication = null, track = null) =>
  String(track?.kind ?? publication?.kind ?? '').trim().toLowerCase();

const resolveTrackSource = (publication = null, track = null) =>
  normalizeSource(publication?.source ?? publication?.track?.source ?? track?.source ?? null);

const isScreenShareSource = (source) =>
  source === 'screen_share' || source === 'screenshare' || source === 'screen';

const isScreenShareAudioSource = (source) =>
  source === 'screen_share_audio' || source === 'screenshareaudio';

const createStream = (tracks = []) => {
  if (typeof MediaStream === 'undefined') return null;
  const normalizedTracks = tracks.filter(Boolean);
  return normalizedTracks.length ? new MediaStream(normalizedTracks) : null;
};

const resolveStreamTracks = (stream = null, kind = null) => {
  if (!stream) return [];
  if (kind === 'audio') return stream.getAudioTracks?.() ?? [];
  if (kind === 'video') return stream.getVideoTracks?.() ?? [];
  return stream.getTracks?.() ?? [];
};

const streamFromTracks = (tracks = [], fallbackStream = null) => {
  const normalizedTracks = tracks.filter(Boolean);
  if (!normalizedTracks.length) return null;
  const fallbackTracks = resolveStreamTracks(fallbackStream);
  const matchesFallback =
    fallbackStream &&
    fallbackTracks.length === normalizedTracks.length &&
    fallbackTracks.every((track, index) => track === normalizedTracks[index]);
  return matchesFallback ? fallbackStream : createStream(normalizedTracks) ?? fallbackStream;
};

const resolveTrackDimensions = (publication = null, track = null) => {
  const settings = track?.getSettings?.() ?? {};
  const dimensions = publication?.dimensions ?? publication?.track?.dimensions ?? track?.dimensions ?? {};
  const width = toFiniteNumber(settings.width ?? dimensions.width);
  const height = toFiniteNumber(settings.height ?? dimensions.height);
  const configuredAspectRatio = toFiniteNumber(
    settings.aspectRatio ?? dimensions.aspectRatio ?? dimensions.aspect_ratio,
  );
  const hasDimensions = width > 0 && height > 0;
  const aspectRatio =
    configuredAspectRatio > 0 ? configuredAspectRatio : hasDimensions ? width / height : null;
  if (!aspectRatio) return null;
  return {
    width: width > 0 ? width : null,
    height: height > 0 ? height : null,
    aspectRatio,
  };
};

const firstDimensions = (entries = []) => {
  for (const entry of entries) {
    const dimensions = resolveTrackDimensions(entry.publication, entry.track);
    if (dimensions) return dimensions;
  }
  return null;
};

export const collectMessengerV2CallParticipantMedia = (participant = null) => {
  const publications =
    participant?.trackPublications?.values?.() ?? participant?.tracks?.values?.() ?? [];
  const entries = Array.from(publications)
    .map((publication) => {
      const track = resolvePublicationTrack(publication);
      if (!track) return null;
      return {
        publication,
        track,
        kind: resolveTrackKind(publication, track),
        source: resolveTrackSource(publication, track),
      };
    })
    .filter(Boolean);

  const audioEntries = entries.filter((entry) => entry.kind === 'audio' && !isScreenShareAudioSource(entry.source));
  const videoEntries = entries.filter((entry) => entry.kind === 'video');
  const screenShareEntries = videoEntries.filter((entry) => isScreenShareSource(entry.source));
  const cameraEntries = videoEntries.filter((entry) => !isScreenShareSource(entry.source));
  const screenShareAudioEntries = entries.filter((entry) =>
    entry.kind === 'audio' && isScreenShareAudioSource(entry.source),
  );

  const allTracks = entries.map((entry) => entry.track);
  const audioTracks = audioEntries.map((entry) => entry.track);
  const cameraTracks = cameraEntries.map((entry) => entry.track);
  const screenShareTracks = screenShareEntries.map((entry) => entry.track);
  const screenShareAudioTracks = screenShareAudioEntries.map((entry) => entry.track);
  const cameraDimensions = firstDimensions(cameraEntries);
  const screenShareDimensions = firstDimensions(screenShareEntries);

  return {
    stream: createStream(allTracks),
    audioStream: createStream(audioTracks),
    videoStream: createStream(cameraTracks),
    screenShareStream: createStream(screenShareTracks),
    screenShareAudioStream: createStream(screenShareAudioTracks),
    audioTracks,
    cameraTracks,
    screenShareTracks,
    screenShareAudioTracks,
    cameraDimensions,
    screenShareDimensions,
    cameraEnabled: cameraEntries.some(({ track, publication }) => track.enabled !== false && track.readyState !== 'ended' && publication.isMuted !== true),
    screenShareEnabled: screenShareEntries.some(({ track, publication }) => track.enabled !== false && track.readyState !== 'ended' && publication.isMuted !== true),
    isSpeaking: participant?.isSpeaking === true,
    soundMuted: participant?.attributes?.sound_muted === 'true',
    audioLevel: Math.max(0, toFiniteNumber(participant?.audioLevel) ?? 0),
  };
};

export const resolveMessengerV2StreamCameraDimensions = (stream = null) => {
  const track = resolveStreamTracks(stream, 'video')[0] ?? null;
  return resolveTrackDimensions(null, track);
};

const hasLiveTrack = (tracks = []) =>
  tracks.some((track) => track?.readyState !== 'ended' && track?.enabled !== false);

export const normalizeMessengerV2CallParticipantSnapshot = (participant = {}) => {
  const publishedMedia = collectMessengerV2CallParticipantMedia(participant);
  const explicitStream = resolveExplicitValue(participant, 'stream', null);
  const explicitAudioStream = resolveExplicitValue(participant, 'audioStream', undefined);
  const explicitVideoStream = resolveExplicitValue(participant, 'videoStream', undefined);
  const explicitScreenShareStream = resolveExplicitValue(
    participant,
    'screenShareStream',
    undefined,
  );
  const explicitScreenShareAudioStream = resolveExplicitValue(
    participant,
    'screenShareAudioStream',
    undefined,
  );

  const combinedStream =
    explicitStream ??
    publishedMedia.stream ??
    streamFromTracks([
      ...resolveStreamTracks(explicitAudioStream, 'audio'),
      ...resolveStreamTracks(explicitVideoStream, 'video'),
    ]);
  const audioTracks =
    publishedMedia.audioTracks.length > 0
      ? publishedMedia.audioTracks
      : resolveStreamTracks(explicitAudioStream ?? combinedStream, 'audio');
  const cameraTracks =
    publishedMedia.cameraTracks.length > 0
      ? publishedMedia.cameraTracks
      : resolveStreamTracks(explicitVideoStream ?? combinedStream, 'video');
  const screenShareTracks =
    publishedMedia.screenShareTracks.length > 0
      ? publishedMedia.screenShareTracks
      : resolveStreamTracks(explicitScreenShareStream, 'video');
  const screenShareAudioTracks =
    publishedMedia.screenShareAudioTracks.length > 0
      ? publishedMedia.screenShareAudioTracks
      : resolveStreamTracks(explicitScreenShareAudioStream, 'audio');

  const audioStream =
    explicitAudioStream === null
      ? null
      : explicitAudioStream ?? streamFromTracks(audioTracks, combinedStream);
  const videoStream =
    explicitVideoStream === null
      ? null
      : explicitVideoStream ?? streamFromTracks(cameraTracks, combinedStream);
  const screenShareStream =
    explicitScreenShareStream === null
      ? null
      : explicitScreenShareStream ??
        publishedMedia.screenShareStream ??
        streamFromTracks(screenShareTracks);
  const screenShareAudioStream =
    explicitScreenShareAudioStream === null
      ? null
      : explicitScreenShareAudioStream ??
        publishedMedia.screenShareAudioStream ??
        streamFromTracks(screenShareAudioTracks);

  return {
    ...participant,
    stream: combinedStream,
    audioStream,
    videoStream,
    screenShareStream,
    screenShareAudioStream,
    audioTracks,
    cameraTracks,
    screenShareTracks,
    screenShareAudioTracks,
    cameraDimensions:
      participant.cameraDimensions ??
      publishedMedia.cameraDimensions ??
      resolveMessengerV2StreamCameraDimensions(videoStream ?? combinedStream),
    screenShareDimensions:
      participant.screenShareDimensions ??
      publishedMedia.screenShareDimensions ??
      resolveMessengerV2StreamCameraDimensions(screenShareStream),
    cameraEnabled: resolveExplicitValue(participant, 'cameraEnabled', hasLiveTrack(cameraTracks)),
    screenShareEnabled: resolveExplicitValue(
      participant,
      'screenShareEnabled',
      hasLiveTrack(screenShareTracks),
    ),
    isSpeaking: participant.isSpeaking === true,
    audioLevel: Math.max(0, toFiniteNumber(participant.audioLevel) ?? 0),
  };
};

export default collectMessengerV2CallParticipantMedia;
